import { Cell, Legend, Pie, PieChart as RPieChart, ResponsiveContainer, Tooltip } from 'recharts'
import { useWidgetDensity } from '../grid/WidgetDensityContext'
import { Sparkline } from './Sparkline'
import { ChartAccessibleTable, ChartFrame } from './ChartFrame'
import { ChartPatternDefs, chartFill } from './ChartPatternDefs'

export interface DonutSlice {
  key: string
  label: string
  value: number
}

export interface DonutChartWrapperProps {
  title: string
  slices: DonutSlice[]
  valueFormatter?: (value: number) => string
  onSliceClick?: (slice: DonutSlice) => void
  height?: number
}

/**
 * AC6 — Monetization outcomes. Caller is responsible for capping to ≤6
 * categories + "other" (design-system.md §5.1) before this renders; the
 * >4-series cycle/shape rule still applies for the palette itself.
 *
 * **Auto-density (IB-311 Slice 1):** `compact` drops the legend (slices are still readable via
 * tooltip + the always-available data table); `micro` collapses to a `Sparkline` of the slice
 * values + the top slice's share.
 */
export function DonutChartWrapper({
  title,
  slices,
  valueFormatter = (v) => String(v),
  onSliceClick,
  height = 260,
}: DonutChartWrapperProps) {
  const { density } = useWidgetDensity()
  const total = slices.reduce((sum, s) => sum + s.value, 0)

  const table = (
    <ChartAccessibleTable
      caption={`${title} — data table`}
      columns={[
        { key: 'label', label: 'Outcome' },
        { key: 'value', label: 'Value', align: 'end' },
        { key: 'share', label: 'Share', align: 'end' },
      ]}
      rows={slices.map((s) => ({
        label: s.label,
        value: valueFormatter(s.value),
        share: total > 0 ? `${((s.value / total) * 100).toFixed(1)}%` : '0%',
      }))}
    />
  )

  const topShare = total > 0 ? [...slices].sort((a, b) => b.value - a.value)[0] : undefined
  const microFallback =
    slices.length > 0 ? (
      <Sparkline
        points={slices.map((s) => ({ key: s.key, label: s.label, value: s.value }))}
        valueFormatter={valueFormatter}
        summaryLabel={topShare && total > 0 ? `${topShare.label}: ${((topShare.value / total) * 100).toFixed(0)}%` : undefined}
      />
    ) : undefined

  return (
    <ChartFrame
      title={title}
      height={height}
      table={table}
      microFallback={microFallback}
      chart={
        <ResponsiveContainer width="100%" height="100%">
          <RPieChart>
            <ChartPatternDefs seriesCount={slices.length} />
            <Tooltip formatter={(value) => valueFormatter(Number(value))} />
            {density === 'full' ? <Legend /> : null}
            <Pie
              data={slices}
              dataKey="value"
              nameKey="label"
              innerRadius="55%"
              outerRadius="80%"
              onClick={(entry) => onSliceClick?.(entry as unknown as DonutSlice)}
              cursor={onSliceClick ? 'pointer' : undefined}
            >
              {slices.map((s, i) => (
                <Cell key={s.key} fill={chartFill(i)} />
              ))}
            </Pie>
          </RPieChart>
        </ResponsiveContainer>
      }
    />
  )
}
